/**
 * "Conductor llegando": claxon en el teléfono del pasajero cuando el conductor está a 300 m o menos
 * de la recogida, o a menos de 1 minuto, lo que ocurra primero. Un solo aviso por viaje.
 * Libre de dependencias de React / React Native para testing directo en Node.
 */
import { GetDistance } from './GeoFunctions.js';

export const DRIVER_ARRIVING_RADIUS_KM = 0.3;
export const DRIVER_ARRIVING_ETA_SECS = 60;
export const DRIVER_ARRIVING_STATUSES = ['ACCEPTED', 'PICKUP_USER'];
export const DRIVER_ARRIVING_EVENT = 'driver_arriving';
/** Debe coincidir con el archivo en mobile-app/assets/sounds y con el canal creado en GetPushToken. */
export const DRIVER_ARRIVING_SOUND = 'claxon_arrived.wav';
export const DRIVER_ARRIVING_CHANNEL = 'driver_arriving';
export const DRIVER_ARRIVING_BANNER_MS = 6000;
/** Un claxon que llega tarde (teléfono sin red) ya no sirve: Expo lo descarta pasado este tiempo. */
export const DRIVER_ARRIVING_PUSH_TTL_SECS = 120;
/**
 * iOS suspende la app en segundo plano: al volver (p. ej. tocando la notificación) RTDB entrega la marca
 * unos segundos después de quedar activa. Ese claxon ya sonó como notificación; no se repite.
 */
export const DRIVER_ARRIVING_RESUME_GRACE_MS = 10000;

export const shouldPlayInAppArriving = ({ appState, activeSinceMs, nowMs }) =>
    appState === 'active' && nowMs - (Number(activeSinceMs) || 0) > DRIVER_ARRIVING_RESUME_GRACE_MS;

/** Mismo factor que tripProgress: la distancia por calle es ≈ 1,4 × la línea recta. */
const ROAD_FACTOR = 1.4;

export const isDeliveryBooking = (booking) =>
    !!(booking && (booking.parcelTypeSelected || booking.deliveryWithBid));

/** Velocidad promedio esperada del viaje, acotada como en tripProgress (20–60 km/h, 30 por defecto). */
export const averageTripSpeedKmh = (booking) => {
    const km = parseFloat(booking && booking.estimateDistance);
    const hours = parseFloat(booking && booking.estimateTime) / 3600;
    if (km > 0 && hours > 0) return Math.max(20, Math.min(60, km / hours));
    return 30;
};

/** `customer_arrival_horn_off` lo copia el servidor desde el perfil del pasajero al crear la reserva. */
export const isArrivalHornEnabled = (booking) => !(booking && booking.customer_arrival_horn_off === true);

export const driverArrivingCheck = (booking, location, distanceFn = GetDistance) => {
    if (!booking || DRIVER_ARRIVING_STATUSES.indexOf(booking.status) === -1) return { notify: false, reason: 'status' };
    if (booking.driverArrivingAt) return { notify: false, reason: 'already_sent' };
    if (isDeliveryBooking(booking)) return { notify: false, reason: 'delivery' };
    const pickup = booking.pickup;
    if (!pickup || pickup.lat == null || pickup.lng == null || !location || location.lat == null || location.lng == null) {
        return { notify: false, reason: 'no_location' };
    }
    const distanceKm = distanceFn(Number(location.lat), Number(location.lng), Number(pickup.lat), Number(pickup.lng));
    if (!Number.isFinite(distanceKm)) return { notify: false, reason: 'no_location' };
    const etaSecs = Math.round(((distanceKm * ROAD_FACTOR) / averageTripSpeedKmh(booking)) * 3600);
    if (distanceKm <= DRIVER_ARRIVING_RADIUS_KM) return { notify: true, reason: 'distance', distanceKm, etaSecs };
    if (etaSecs <= DRIVER_ARRIVING_ETA_SECS) return { notify: true, reason: 'eta', distanceKm, etaSecs };
    return { notify: false, reason: 'far', distanceKm, etaSecs };
};

/** Push al pasajero. Sin claxon (apagado en Ajustes) va con el sonido normal y el canal por defecto. */
export const buildDriverArrivingPush = (booking, texts = {}) => {
    const horn = isArrivalHornEnabled(booking);
    return {
        title: texts.title || 'Tu conductor está llegando',
        msg: texts.msg || 'Prepárate, tu conductor está a menos de un minuto.',
        screen: 'BookedCab',
        params: { bookingId: booking.id, event: DRIVER_ARRIVING_EVENT },
        ttl: DRIVER_ARRIVING_PUSH_TTL_SECS,
        ...(horn ? { sound: DRIVER_ARRIVING_SOUND, channelId: DRIVER_ARRIVING_CHANNEL } : {}),
    };
};

export const isDriverArrivingPush = (data) =>
    !!(data && data.params && data.params.event === DRIVER_ARRIVING_EVENT);

/**
 * App del pasajero: detecta la reserva cuya marca `driverArrivingAt` apareció desde la observación anterior.
 * Con `prev` null (primera lectura al abrir la app) no avisa: ese claxon ya sonó como notificación.
 */
export const diffDriverArriving = (prev, activeBookings) => {
    const next = {};
    let arrived = null;
    (Array.isArray(activeBookings) ? activeBookings : []).forEach((booking) => {
        if (!booking || !booking.id) return;
        const flagged = !!booking.driverArrivingAt;
        next[booking.id] = flagged;
        if (
            !arrived &&
            flagged &&
            prev &&
            prev[booking.id] === false &&
            DRIVER_ARRIVING_STATUSES.indexOf(booking.status) !== -1 &&
            !isDeliveryBooking(booking)
        ) {
            arrived = booking;
        }
    });
    return { next, arrived };
};
